import { ApiClient } from '@api/api-configuration';
import { skipGlobalErrorHandler } from '@context/api/ApiInterceptorProvider';
import { BillingEntityTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import { ClientPopulated } from '@api/READ_ONLY/client_api/Api';
import dayjs from '@app/utils/dayjs-setup';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import {
  CARD_WINDOW_DAYS_BACK,
  CARD_WINDOW_DAYS_FORWARD,
  type CardAttachment,
  type CardVisit,
} from './clientCard.model';

/**
 * Back far enough that a vital measured every other week has a series worth
 * plotting, and forward far enough to hold the next visits of a client seen once a
 * week. Everything on the page — timeline, vitals, notes, care team, the header's
 * counts — is derived from this one window, so widening it widens all of them at
 * once and the reader never sees two panels disagreeing about what "recently" is.
 * The two numbers live in the model because the panels quote them to the reader.
 */

/**
 * One page, and the ceiling is deliberate. A daily-care client generates about 180
 * visits across this window; the endpoint's own maximum is 1000. A client busier
 * than the page loses the OLDEST visits, because the fetch asks for newest first —
 * the header's counts and the next visit stay correct, and it is the far end of the
 * timeline that thins out.
 */
export const CARD_VISITS_PAGE_SIZE = 400;

/** Client folders hold documents, not thousands of them; one page is the whole folder. */
const CLIENT_ATTACHMENTS_PAGE_SIZE = 200;

const CLIENT_CARD_KEYS = {
  client: 'client-card-client',
  visits: 'client-card-visits',
  attachments: 'client-card-attachments',
  balance: 'client-card-balance',
} as const;

/** A 404 from the balance lookup: this client has no billing account yet. */
const isMissingBillingAccount = (error: unknown): boolean => (error as AxiosError | null)?.response?.status === 404;

/**
 * Everything the client card renders, from one client fetch, one visits fetch, one
 * attachments fetch and the client's balance.
 *
 * Each is reported separately rather than collapsed into a single loading flag: the
 * balance living in another service must not keep the timeline off the screen, and
 * a balance that failed to load has to read as unknown rather than as zero.
 */
const useClientCard = (clientId: number, { includeBilling = true }: { includeBilling?: boolean } = {}) => {
  const window = useMemo(() => {
    const now = dayjs();
    return {
      from: now.subtract(CARD_WINDOW_DAYS_BACK, 'day').startOf('day').toISOString(),
      to: now.add(CARD_WINDOW_DAYS_FORWARD, 'day').endOf('day').toISOString(),
    };
  }, []);

  const clientQuery = useQuery({
    queryKey: [CLIENT_CARD_KEYS.client, clientId],
    enabled: Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => (await ApiClient.clientApi.clients.getClientById(clientId)).data,
  });

  const visitsQuery = useQuery({
    queryKey: [CLIENT_CARD_KEYS.visits, clientId, window.from, window.to],
    enabled: Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      // `sort` is sent explicitly. The endpoint's own default is `visit_id`, not the
      // start time, so without this a client busier than one page keeps the LOWEST
      // ids — the oldest rows — and drops the newest, which are the scheduled ones:
      // the next visit reads as none, the counts under-report and the care team
      // loses whoever is coming.
      const response = await ApiClient.visitsApi.visits.listVisits({
        client_id: clientId,
        start_time_from: window.from,
        start_time_to: window.to,
        sort: '-start_time',
        limit: CARD_VISITS_PAGE_SIZE,
        offset: 0,
      });
      return {
        visits: response.data?.data ?? [],
        total: response.data?.total ?? 0,
      };
    },
  });

  const attachmentsQuery = useQuery({
    queryKey: [CLIENT_CARD_KEYS.attachments, clientId],
    enabled: Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      const response = await ApiClient.clientApi.clients.listClientAttachments(clientId, {
        offset: 0,
        limit: CLIENT_ATTACHMENTS_PAGE_SIZE,
      });
      return (response.data?.data ?? []) as unknown as CardAttachment[];
    },
  });

  const balanceQuery = useQuery({
    queryKey: [CLIENT_CARD_KEYS.balance, clientId],
    // Not fetched at all for a reader who cannot see billing: the endpoint refuses
    // them, and a 403 in the console is not a thing to make a coordinator produce.
    enabled: includeBilling && Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      try {
        return (
          await ApiClient.billingApi.billing.getEntityBalance(
            BillingEntityTypeEnum.Client,
            clientId,
            // The 404 below is an ANSWER, not a fault, but the interceptor toasts
            // every non-2xx before this catch runs. A real failure still reads as
            // "balance unavailable" on the tile, so nothing is lost by silencing it.
            skipGlobalErrorHandler({})
          )
        ).data;
      } catch (error) {
        // A client who has never been billed has no billing account, and the
        // endpoint answers 404 rather than zero. That is "nothing has been charged
        // yet" — true of every client on their first day — so it is caught here and
        // becomes an answer. Left as an error it would be retried, and reported to
        // the reader as a balance nobody could read.
        if (isMissingBillingAccount(error)) return null;
        throw error;
      }
    },
  });

  const hasNoBillingAccount = balanceQuery.isSuccess && balanceQuery.data === null;

  const visits: CardVisit[] = visitsQuery.data?.visits ?? [];

  return {
    client: (clientQuery.data ?? null) as ClientPopulated | null,
    isClientLoading: clientQuery.isLoading,
    clientError: clientQuery.error,

    visits,
    /** How many visits the window really holds, which is more than `visits` once it is busier than a page. */
    visitsTotal: visitsQuery.data?.total ?? visits.length,
    areVisitsLoading: visitsQuery.isLoading,
    visitsError: visitsQuery.error,

    clientAttachments: attachmentsQuery.data ?? [],
    areAttachmentsLoading: attachmentsQuery.isLoading,
    attachmentsError: attachmentsQuery.error,

    /**
     * `null` while loading and when the lookup genuinely failed — never 0, which
     * would read as settled up. A client with no billing account is 0, because that
     * is what they owe.
     */
    balance: balanceQuery.data?.balance ?? (hasNoBillingAccount ? 0 : null),
    lastMovement: balanceQuery.data?.lastMovement ?? null,
    hasNoBillingAccount,
    isBalanceLoading: balanceQuery.isLoading,
    balanceError: balanceQuery.error,

    window,
  };
};

export default useClientCard;
